import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedDate } from 'react-intl';
import { withTheme } from 'styled-components';

import {
  List,
  ListHeader,
  ListHeaderItem,
  ListBody,
  ListItem,
  ListItemColumn,
  AvatarStack,
  HOCs,
} from '@pearsontechnology/aero-components';

import BaseMoreButton from 'components/MoreButton';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { commandWorkspaceCohortCollaboratorsSummary } from 'containers/ConnectionManager/actions';
import enhanceWithNavigation from 'hocs/enhanceWithNavigation';

import AvatarsPlaceholder from 'Workspace/components/AvatarsPlaceholder';
import CollaboratorsSummaryProvider from 'Workspace/containers/CollaboratorsSummaryProvider';
import { dispatch } from 'pluginServices/store';
import { makeAvatarsFromCollaborators } from 'Workspace/helpers';
import { selectCohortCollaborators } from 'Workspace/Cohorts/store/selectors';
import { setCohortList } from 'Workspace/Cohorts/store/actions';
import { cohortSortKeys } from 'Workspace/Cohorts/components/constants';

import ActionMenu from './ActionMenu';
import ProductModelPill from './ProductModelPill';
import { resolveProductModel } from './productModelConstants';

import {
  CohortColumnItem,
  CohortIcon,
  SortButton,
} from './styles';

import messages from './messages';

const LinkListItem = enhanceWithNavigation(ListItem);

const { enhanceWithPopper } = HOCs;
const MoreButtonWithPopper = enhanceWithPopper(BaseMoreButton);

const CohortsList = ({ cohorts, theme, sortState, sortBy }) => {

  useEffect(() => () => dispatch(setCohortList(undefined)), []);

  return (
    <List>
      <ListHeader>
        <ListItemColumn colWidth="50%" colWidthMobile="90%">
          <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderTextName)}</ListHeaderItem>
          <SortButton id="name" sort={sortState} onClick={() => sortBy(cohortSortKeys.NAME, 'string')} />
        </ListItemColumn>
        <ListItemColumn colWidth="230px" colWidthMobile="50%">
          <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderTextDate)}</ListHeaderItem>
          <SortButton id="startDate" sort={sortState} onClick={() => sortBy(cohortSortKeys.START_DATE, "date")} />
        </ListItemColumn>
        <ListItemColumn colWidth="130px" colWidthMobile="50%">
          <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderTextProductModel)}</ListHeaderItem>
        </ListItemColumn>
        <ListItemColumn colWidth="115px" colWidthMobile="50%">
          <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderTextStudents)}</ListHeaderItem>
        </ListItemColumn>
        <ListItemColumn colWidth="115px" colWidthMobile="50%">
          <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderTextLessons)}</ListHeaderItem>
        </ListItemColumn>
        <ListItemColumn colWidth="130px" colWidthMobile="50%">
          <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderTextInstructors)}</ListHeaderItem>
        </ListItemColumn>
        <ListItemColumn colWidth="34px" colWidthMobile="34px" grow="0">
          <div style={{ width: 34 }} />
        </ListItemColumn>
      </ListHeader>
      <ListBody>
        {
          cohorts
            .filter((cohort) => !cohort.finishedDate)
            .map((cohort) => (
              <LinkListItem
                key={`cohort-item-${cohort.cohortId}`}
                path={`/classes/${cohort.cohortId}`}
              >
                <ListItemColumn colWidth="50%" colWidthMobile="90%">
                  <CohortColumnItem>
                    <CohortIcon />
                    {cohort.name}
                  </CohortColumnItem>
                </ListItemColumn>
                <ListItemColumn colWidth="230px" colWidthMobile="50%">
                  <CohortColumnItem>
                    <span>
                      <FormattedDate
                        value={new Date(cohort.startDate)}
                        year="numeric"
                        month="short"
                      />

                      {cohort.endDate &&
                        <Fragment>
                          <span> &mdash; </span>
                          <FormattedDate
                            value={new Date(cohort.endDate)}
                            year="numeric"
                            month="short"
                          />
                        </Fragment>
                      }
                    </span>
                  </CohortColumnItem>
                </ListItemColumn>
                <ListItemColumn colWidth="130px" colWidthMobile="50%">
                  <CohortColumnItem>
                    <ProductModelPill productModel={resolveProductModel(cohort)} />
                  </CohortColumnItem>
                </ListItemColumn>
                <ListItemColumn colWidth="115px" colWidthMobile="50%">
                  <CohortColumnItem>
                    {cohort.enrollmentsCount || 0}
                  </CohortColumnItem>
                </ListItemColumn>
                <ListItemColumn colWidth="115px" colWidthMobile="50%">
                  <CohortColumnItem>
                    0
                  </CohortColumnItem>
                </ListItemColumn>
                <ListItemColumn colWidth="130px" colWidthMobile="50%">
                  <CohortColumnItem instructors>
                    <CollaboratorsSummaryProvider
                      id={cohort.cohortId}
                      fetchCollaboratorsCommand={commandWorkspaceCohortCollaboratorsSummary}
                      collaboratorsSelector={selectCohortCollaborators}
                      render={(collaborators) => collaborators ?
                        <AvatarStack
                          hideButton
                          avatarBorderColor={theme['color.surface']}
                          avatars={makeAvatarsFromCollaborators(collaborators)}
                        /> :
                        <AvatarsPlaceholder />
                      }
                    />
                  </CohortColumnItem>
                </ListItemColumn>
                <ListItemColumn colWidth="34px" colWidthMobile="34px" grow="0">
                  <CohortColumnItem onClick={(e) => e.stopPropagation()}>
                    <MoreButtonWithPopper placement="bottom-end" hidepopperonclick>
                      <ActionMenu cohort={cohort} />
                    </MoreButtonWithPopper>
                  </CohortColumnItem>
                </ListItemColumn>
              </LinkListItem>
            )
            )
        }
      </ListBody>
    </List>
  );
};

CohortsList.propTypes = {
  cohorts: PropTypes.array,
  theme: PropTypes.object,
  sortState: PropTypes.object,
  sortBy: PropTypes.func,
};

export default withTheme(CohortsList);
